'use client'

/**
 * Tables and the distribution figure for /data/energy-index — brand-standard
 * treatment matching the price-index series tables.
 *
 * White card, gray-200 border, rounded-xl, gray-50 uppercase header row,
 * hairline row dividers, right-aligned tabular numerals, and an
 * `overflow-x-auto` scroll container so wide tables never break the page.
 * Below-threshold types are rendered muted and explicitly flagged — disclosed,
 * never hidden, never presented as published benchmarks.
 *
 * Every value arrives via components/seo/energy-index-summary.ts (the single
 * importer of app/data/_data/energy-index.json). Server components — no JS. The
 * figure is inline SVG (no client charting library) and theming-neutral on a
 * white card.
 */
import { ENERGY_REPORT_SVG_FONT_FAMILY } from './energy-report-font'
import { ChartHoverLayer } from '@/components/data/chart-hover-layer'
import {
  type TypeRow,
  type BrandLeader,
  type HistogramBin,
} from '@/components/seo/energy-index-summary'
import { FridgeWordmark } from '@/components/brand-kit/data-figures'
import { FigureActions } from './figure-actions'
import { SortableTable, type SortableColumn } from './sortable-table'
import { EntityLink, type LinkTarget } from './entity-link'

/** Rows plus the live page the server resolved for the row's entity (FRIDGE-334). */
export type LinkedTypeRow = TypeRow & { link?: LinkTarget }
export type LinkedBrandLeader = BrandLeader & { link?: LinkTarget }

function PublishedPill() {
  return (
    <span className="inline-block rounded-full bg-[#f2f7ff] text-[#0469f0] text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
      Published
    </span>
  )
}

function BelowThresholdPill() {
  return (
    <span className="inline-block rounded-full bg-gray-100 text-[#697080] text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
      Below threshold
    </span>
  )
}

function fmtInt(n: number): string {
  return n.toLocaleString('en-US')
}

/** Efficiency benchmark by appliance type — the headline table (click-to-sort). */
export function EnergyTypeTable({ rows }: { rows: LinkedTypeRow[] }) {
  const columns: SortableColumn<LinkedTypeRow>[] = [
    {
      key: 'type',
      header: 'Appliance type',
      sortValue: (r) => r.type,
      render: (r) => (
        <EntityLink link={r.link} className="font-semibold">
          {r.type}
        </EntityLink>
      ),
    },
    { key: 'n', header: 'Records', numeric: true, sortValue: (r) => r.n, render: (r) => <span className={r.below_publication_threshold ? '' : 'font-bold'}>{fmtInt(r.n)}</span> },
    { key: 'median_kwh', header: 'Median kWh/yr', numeric: true, sortValue: (r) => r.median_kwh, render: (r) => <span className={r.below_publication_threshold ? '' : 'font-semibold'}>{fmtInt(r.median_kwh)}</span> },
    { key: 'range', header: 'Range (kWh/yr)', numeric: true, sortValue: (r) => r.min_kwh, render: (r) => <span className="text-gray-500">{fmtInt(r.min_kwh)}–{fmtInt(r.max_kwh)}</span> },
    { key: 'per_ft3', header: 'kWh/yr per ft³', numeric: true, sortValue: (r) => r.median_kwh_per_ft3, render: (r) => r.median_kwh_per_ft3.toFixed(1) },
    { key: 'better', header: 'Better than federal', numeric: true, sortValue: (r) => r.median_pct_better ?? -1, render: (r) => (r.median_pct_better === null ? '—' : `${r.median_pct_better}%`) },
    { key: 'most_efficient', header: 'Most Efficient', numeric: true, sortValue: (r) => r.most_efficient_share, render: (r) => `${r.most_efficient_share.toFixed(1)}%` },
    { key: 'status', header: 'Status', sortValue: (r) => (r.below_publication_threshold ? 1 : 0), render: (r) => (r.below_publication_threshold ? <BelowThresholdPill /> : <PublishedPill />) },
  ]
  return (
    <SortableTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.type}
      muted={(r) => r.below_publication_threshold}
      defaultSortKey="median_kwh"
      defaultDir="asc"
      minWidth={880}
      caption="Refrigerator energy-efficiency benchmark by appliance type — sortable | Fridge.com"
    />
  )
}

/** Brand efficiency leaders — brands with the deepest median margin (click-to-sort). */
export function BrandLeaderTable({ rows }: { rows: LinkedBrandLeader[] }) {
  const columns: SortableColumn<LinkedBrandLeader>[] = [
    {
      key: 'brand',
      header: 'Brand',
      sortValue: (r) => r.brand,
      render: (r) => (
        <EntityLink link={r.link} className="font-semibold">
          {r.brand}
        </EntityLink>
      ),
    },
    { key: 'n', header: 'Certification records', numeric: true, sortValue: (r) => r.n, render: (r) => <span className="text-gray-600">{fmtInt(r.n)}</span> },
    { key: 'median_kwh', header: 'Median kWh/yr', numeric: true, sortValue: (r) => r.median_kwh, render: (r) => fmtInt(r.median_kwh) },
    { key: 'better', header: 'Better than federal', numeric: true, sortValue: (r) => r.median_pct_better, render: (r) => <span className="font-bold">{r.median_pct_better}%</span> },
    { key: 'most_efficient', header: 'Most Efficient', numeric: true, sortValue: (r) => r.most_efficient_share, render: (r) => <span className="text-gray-600">{r.most_efficient_share.toFixed(1)}%</span> },
  ]
  return (
    <SortableTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.brand}
      defaultSortKey="better"
      defaultDir="desc"
      minWidth={560}
      caption="Brand refrigerator energy-efficiency leaders — sortable | Fridge.com"
    />
  )
}

/**
 * Distribution of certified annual energy use, as an inline-SVG column chart.
 * One column per 100 kWh/yr band; heights scale to the tallest band. Pure SVG,
 * server-rendered, white background, brand blue columns with an orange marker on
 * the band that contains the overall median.
 */
export function EnergyDistributionFigure({
  bins,
  medianKwh,
}: {
  bins: HistogramBin[]
  medianKwh: number
}) {
  const W = 720
  const H = 300
  const padL = 44
  const padR = 16
  const padT = 16
  const padB = 46
  const plotW = W - padL - padR
  const plotH = H - padT - padB
  const maxN = Math.max(...bins.map((b) => b.n), 1)
  const gap = 8
  const colW = (plotW - gap * (bins.length - 1)) / bins.length

  // Horizontal gridlines at 0/25/50/75/100% of the tallest band.
  const gridTicks = [0, 0.25, 0.5, 0.75, 1]

  return (
    <figure className="bg-white border border-gray-200 rounded-xl p-5 sm:p-6">
      {/* Mobile-first stack: full-width title + caption on their own rows, the
          share toolbar below — so the title reads in multi-word lines and the
          icon row never fights it. Inline title-left / toolbar-right from sm up.
          Zero clipping at 375/390 (owner spec 07/20). */}
      <figcaption className="mb-4 flex flex-col gap-y-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-x-4 sm:gap-y-2">
        <div className="min-w-0">
          <h3 className="text-base font-bold text-gray-900">
            Distribution of certified annual energy use
          </h3>
          <p className="mt-1 text-sm text-gray-600 leading-relaxed">
            Every certified refrigerator and freezer in the release, grouped by annual energy use in
            100&nbsp;kWh/yr bands. The orange marker sits on the band containing the overall median
            of {medianKwh.toLocaleString('en-US')}&nbsp;kWh/yr.
          </p>
        </div>
        <FigureActions
          slug="refrigerator-energy-use-distribution"
          shareTitle="Distribution of certified refrigerator energy use"
          anchorId="distribution"
        />
      </figcaption>
      <ChartHoverLayer>
      {/* The chart is wider than a phone and scrolls; a scroll region with no
          focusable descendant is unreachable by keyboard (axe
          `scrollable-region-focusable`, serious — the rendered gate in
          scripts/accessibility-axe-gate.ts). tabIndex + role="region" +
          aria-label is the pattern series-tables.tsx and canvas-moving-band.tsx
          already carry for the same reason; the static rule cannot see that the
          element scrolls, which is the whole reason the attribute is needed. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- axe scrollable-region-focusable; see the note above and series-tables.tsx (FRIDGE-412) */}
      <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Energy-use distribution — scroll chart">
        <svg
          fontFamily={ENERGY_REPORT_SVG_FONT_FAMILY}
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          style={{ minWidth: 720, height: 'auto' }}
          data-chart-interactive
          role="img"
          aria-label={`Column chart: count of certification records in each 100 kWh per year band, from ${bins[0].lower} to ${bins[bins.length - 1].upper} kWh per year. The tallest band holds ${maxN} records | Fridge.com`}
        >
          <FridgeWordmark x={W - padR} y={2} anchor="end" size={13} />
          {/* gridlines + y labels */}
          {gridTicks.map((t) => {
            const y = padT + plotH - t * plotH
            return (
              <g key={t}>
                <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#eef1f4" strokeWidth={1} />
                <text x={padL - 8} y={y + 3} textAnchor="end" fontSize={10} fill="#9aa4b2">
                  {Math.round(t * maxN)}
                </text>
              </g>
            )
          })}
          {/* columns */}
          {bins.map((b, i) => {
            const x = padL + i * (colW + gap)
            const h = (b.n / maxN) * plotH
            const y = padT + plotH - h
            const isMedianBand = medianKwh >= b.lower && medianKwh < b.upper
            return (
              <g
                key={b.lower}
                className="cursor-pointer"
                data-pt
                data-l={`${b.lower}–${b.upper} kWh/yr`}
                data-v={`${b.n} records`}
                tabIndex={0}
                aria-label={`${b.lower} to ${b.upper} kWh per year: ${b.n} records`}
              >
                <title>{`${b.lower}–${b.upper} kWh/yr: ${b.n} records`}</title>
                <rect
                  x={x}
                  y={y}
                  width={colW}
                  height={h}
                  rx={3}
                  fill={isMedianBand ? '#ff9951' : '#0469f0'}
                  className="hover:opacity-80 transition-opacity"
                />
                <text
                  x={x + colW / 2}
                  y={y - 5}
                  textAnchor="middle"
                  fontSize={10}
                  fontWeight={600}
                  fill={isMedianBand ? '#b25410' : '#0469f0'}
                >
                  {b.n}
                </text>
                <text
                  x={x + colW / 2}
                  y={H - padB + 16}
                  textAnchor="middle"
                  fontSize={10}
                  fill="#6b7280"
                >
                  {b.lower}
                </text>
              </g>
            )
          })}
          {/* baseline + axis titles */}
          <line x1={padL} y1={padT + plotH} x2={W - padR} y2={padT + plotH} stroke="#d1d7de" strokeWidth={1} />
          <text x={padL} y={H - 6} fontSize={10} fill="#9aa4b2">
            Annual energy use (kWh/yr, lower bound of each 100 kWh band)
          </text>
        </svg>
      </div>
      </ChartHoverLayer>
    </figure>
  )
}
